import { Suspense, useContext, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { SheetCategory, Spend } from '../../../../lib/Types'
import { useBudget, useReconciliationExpenses } from '../../lib/Hooks/Budget'
import { useCategories } from '../../lib/Hooks/Categories'
import './../Budget.css'
import ReconciliationSpends from './ReconciliationSpends'

import { BudgetContext } from '../../../../lib/Context'
import ReconciliationModal from './ReconciliationModal'

const BudgetReconciliation = () => {
  const { t } = useTranslation(['budget'])
  const { data: budget } = useBudget()
  const { data: categories } = useCategories(budget!)
  const { reconciliationYear, setReconciliationToEdit, reconciliationToEdit } = useContext(BudgetContext)
  const { data: spends } = useReconciliationExpenses(budget!, reconciliationYear)

  const refSticky = useRef<HTMLTableSectionElement>(null)

  /**
   * Split the spends into categories, by moving spends with a parent into the parent spends children array.
   */
  const categorizeSpends = (category: SheetCategory): Spend[] => {
    const categorizedSpends: { [k: number]: Spend } = {}

    const spendsInCategory = spends!.filter((item) => {
      return item.category.id === category.id
    })

    // Start by building the list of parent spends
    spendsInCategory.forEach((item) => {
      if (!item.parent_id) {
        categorizedSpends[item.id] = item
        categorizedSpends[item.id].children = []
      }
    })

    // Add all children to the their parents.
    spendsInCategory.forEach((item) => {
      if (item.parent_id && categorizedSpends[item.parent_id]) {
        categorizedSpends[item.parent_id].children.push(item)
      }
    })

    // Make sure the spends in the new array are ordered by their item.order property
    const orderedSpendsInCategory = Object.values(categorizedSpends).sort(
      (a, b) => a.order - b.order,
    )

    // Order the children of the parent spends
    orderedSpendsInCategory.forEach((item) => {
      item.children.sort((a, b) => a.order - b.order)
    })

    return orderedSpendsInCategory
  }

  return (
    <>
      <ReconciliationModal
        hideModal={() => setReconciliationToEdit((null))}
        reconciliation={reconciliationToEdit ? reconciliationToEdit.reconciliation : null}
        spend={reconciliationToEdit ? reconciliationToEdit!.spend : null}
      />
      <table
        className={'table-budget min-w-full col-3'}
      >
        <thead ref={refSticky} className={'bg-gray-300 border border-gray-300'}>
          <tr>
            <th
              scope='col'
              className='text-sm font-medium text-gray-900 px-6 py-4 text-left'
            >
              {t('budget:shared.titleColumn')}
            </th>
            <th
              scope='col'
              className='text-sm font-medium text-gray-900 px-6 py-4 text-left'
            >
              {t('budget:budgetReconciliation.budgetColumn')}
            </th>
            <th
              scope='col'
              className='text-sm font-medium text-gray-900 px-6 py-4 text-left'
            >
              {t('budget:budgetReconciliation.actualColumn')}
            </th>
          </tr>
        </thead>
      </table>
      <Suspense fallback={(
        <div className="animate-pulse">
          <table
            className={'min-w-full table-budget col-3'}
          >
            <tbody>
              <tr className='bg-gray-100 border-b border-x border-gray-300'>
                <th className='px-6 py-4 text-left'>
                  <div className="h-2 w-32 rounded bg-gray-200"></div>
                </th>
                <th className='px-6 py-4 text-left'>
                  <div className="h-2 w-32 rounded bg-gray-200"></div>
                </th>
                <th className='px-6 py-4 text-left'>
                  <div className="h-2 w-32 rounded bg-gray-200"></div>
                </th>
              </tr>
            </tbody>
          </table>
          <table
            className={'min-w-full table-budget col-3'}
          >
            <tbody>
              <tr
                className={'bg-white border border-gray-300'}
              >
                <td className='px-6 py-4 whitespace-nowrap'>
                  <div className="h-2 w-32 rounded bg-gray-200"></div>
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <div className="h-2 w-32 rounded bg-gray-200"></div>
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <div className="h-2 w-32 rounded bg-gray-200"></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )
      }>
        {
          categories!.map((category) => (
            <ReconciliationSpends
              key={category.id}
              category={category}
              spends={categorizeSpends(category)}
            />
          ))
        }
      </Suspense >
    </>
  )
}

export default BudgetReconciliation
